import { useCallback, useState } from 'react';

/**
 * useState backed by a Web Storage area, namespaced under `commandersalt:pref:`.
 * Reads/writes are wrapped in try/catch so a disabled or full storage (private
 * mode, quota) degrades to plain in-memory state instead of throwing.
 *
 * API mirrors useState: returns `[value, setValue]`, and setValue accepts a
 * value or an updater function.
 *
 * Two flavors are exported:
 *  - `useStickyPreference` (localStorage) — persists across tabs AND sessions.
 *    Use ONLY for genuine cross-session user *preferences* (e.g. "Include
 *    Reserved List"), NOT for per-search/per-deck filter state.
 *  - `useSessionPreference` (sessionStorage) — persists within the current tab
 *    session only (survives component remounts + reloads, clears when the tab
 *    or browser closes). Use for transient per-session UI state, e.g. which
 *    advanced-filter sub-panels are expanded.
 */

const PREFIX = 'commandersalt:pref:';

function readStored(getStorage, key, fallback) {
    try {
        const raw = getStorage().getItem(PREFIX + key);
        return raw == null ? fallback : JSON.parse(raw);
    } catch {
        return fallback;
    }
}

function usePersistentState(getStorage, key, defaultValue) {
    const [value, setValue] = useState(() => readStored(getStorage, key, defaultValue));

    const set = useCallback(
        (next) => {
            setValue((prev) => {
                const resolved = typeof next === 'function' ? next(prev) : next;
                try {
                    getStorage().setItem(PREFIX + key, JSON.stringify(resolved));
                } catch {
                    // Persisting failed (private mode / quota) — keep the
                    // in-memory value so the UI still works this session.
                }
                return resolved;
            });
        },
        // getStorage is a stable module-level reference per hook.
        [key]
    );

    return [value, set];
}

const localStore = () => window.localStorage;
const sessionStore = () => window.sessionStorage;

/**
 * @template T
 * @param {string} key      Stable preference key, e.g. 'includeReservedList'.
 * @param {T} defaultValue  Value used when nothing is stored yet.
 * @returns {[T, (next: T | ((prev: T) => T)) => void]}
 */
export function useStickyPreference(key, defaultValue) {
    return usePersistentState(localStore, key, defaultValue);
}

/**
 * Session-scoped sibling of {@link useStickyPreference} — same API, but the
 * value lives in sessionStorage so it never carries over to a future session.
 *
 * @template T
 * @param {string} key
 * @param {T} defaultValue
 * @returns {[T, (next: T | ((prev: T) => T)) => void]}
 */
export function useSessionPreference(key, defaultValue) {
    return usePersistentState(sessionStore, key, defaultValue);
}
